import { useCallback, useEffect, useRef } from 'react';
import { fetchProjectsByIds } from '@/directus/services/common/projects-by-ids/fetch';
import { ISearch, ISelectedFilters, useFiltersStore } from '@/store/useFiltersStore';
import { TLangIso } from '@/types/global';
import { usePageStore } from '@/store/usePageStore';

interface UseLazyLoadProjectsProps {
  // refLoadMoreListener: RefObject<HTMLDivElement | null>;
  langIso: TLangIso;
  search: ISearch;
  selectedFilters: ISelectedFilters;
  updateLazyLoadedProjectsLists: () => void;
}

export function useLazyLoadProjects({
  langIso,
  search,
  selectedFilters,
  updateLazyLoadedProjectsLists,
}: UseLazyLoadProjectsProps) {
  const setLazyLoadedProjectsLists = useFiltersStore((state) => state.setLazyLoadedProjectsLists);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Fetch logic reused by both observer and interval
  const fetchMoreProjects = useCallback(async () => {
    const {
      isProjectsLazyLoadFinished,
      isCurrentlyFetchingProjects,
      nextBufferLazyLoadedProjects,
      paginationLazyLoad,
      projectsIDs,
    } = useFiltersStore.getState();

    if (isProjectsLazyLoadFinished.isFinished || isCurrentlyFetchingProjects) return;

    // constant to avoid multiple duplicated fetches
    useFiltersStore.setState({ isCurrentlyFetchingProjects: true });

    // The next projects to fetch based on the current pagination
    const projectsIDsToFetch =
      projectsIDs?.notArchived.slice(
        paginationLazyLoad * 12,
        (paginationLazyLoad + 1) * 12
      ) || [];

    // Load next buffer if more ids to fetch. This way load is faster and we keep proj fetch from ids in the background
    if ((nextBufferLazyLoadedProjects?.length ?? 0) > 0 && projectsIDsToFetch.length > 0) {
      setLazyLoadedProjectsLists([{ projects: nextBufferLazyLoadedProjects ?? [] }]);
      updateLazyLoadedProjectsLists();
    }

    // If no more ids to fetch and no more buffered projects, we finish the lazy load
    if (
      projectsIDsToFetch.length === 0 &&
      (!nextBufferLazyLoadedProjects || nextBufferLazyLoadedProjects.length === 0)
    ) {
      useFiltersStore.setState({
        nextBufferLazyLoadedProjects: [],
        isProjectsLazyLoadFinished: {
          isFinished: true,
          triggerer: 'endLoad',
        },
        isCurrentlyFetchingProjects: false,
      });
      updateLazyLoadedProjectsLists();
      return;
    // If no more ids to fetch but there is a next buffer, load the last buffer and finish lazy load
    } else if (projectsIDsToFetch.length === 0 && (nextBufferLazyLoadedProjects && nextBufferLazyLoadedProjects.length > 0)) {
      setLazyLoadedProjectsLists([{ projects: nextBufferLazyLoadedProjects ?? [] }]);
      updateLazyLoadedProjectsLists();
      useFiltersStore.setState({
        nextBufferLazyLoadedProjects: [],
        isProjectsLazyLoadFinished: {
          isFinished: true,
          triggerer: 'endLoad',
        },
        isCurrentlyFetchingProjects: false,
      });
      return;
    }

    if (useFiltersStore.getState().isProjectsLazyLoadFinished.isFinished) return;

    // If we are here there are more ids to fetch to load on the buffered list
    const nextBuffer = await fetchProjectsByIds(projectsIDsToFetch, langIso);

    useFiltersStore.setState({
      nextBufferLazyLoadedProjects: nextBuffer,
      paginationLazyLoad: paginationLazyLoad + 1,
      isCurrentlyFetchingProjects: false,
    });

    updateLazyLoadedProjectsLists();
  }, [langIso, updateLazyLoadedProjectsLists, setLazyLoadedProjectsLists]);

  // Observer setup
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        if (!isVisible) return;

        const isUserLoggedIn = usePageStore.getState().isUserLogged;
        const imageSearchValue = useFiltersStore.getState().imageSearch.value;
        const isImageWithProjectsList: boolean = (typeof imageSearchValue !== 'undefined' && imageSearchValue !== '') && isUserLoggedIn;

        if (isVisible && !isImageWithProjectsList) {
          fetchMoreProjects();
        }
      },
      {
        threshold: 1,
        rootMargin: '0px',
      }
    );

    observer.observe(node);
    observerRef.current = observer;

    return () => observer.disconnect();
  }, [langIso, search, selectedFilters, updateLazyLoadedProjectsLists, fetchMoreProjects]);

  // Fallback interval for case when observer is still in view and is not triggering
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    const interval = setInterval(() => {
      const {
        isProjectsLazyLoadFinished,
        isCurrentlyFetchingProjects,
      } = useFiltersStore.getState();

      if (isProjectsLazyLoadFinished.isFinished || isCurrentlyFetchingProjects) return;

      const sentinel = loadMoreRef.current;
      if (!sentinel) return;

      const rect = sentinel.getBoundingClientRect();
      const isVisible =
        rect.top >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

      const isUserLoggedIn = usePageStore.getState().isUserLogged;
      const isImageWithProjectsList = useFiltersStore.getState().imageSearch?.value !== '' && isUserLoggedIn;

      if (isVisible && (!isImageWithProjectsList)) {
        fetchMoreProjects();
      }
    }, 750);

    return () => clearInterval(interval);
  }, [langIso, fetchMoreProjects]);

  return { loadMoreRef };
}